import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { DEFAULT_LANGUAGE_CODE, DEFAULT_LANGUAGE_ID, LOOKING_FOR_JOB } from 'shared/constants';
import useForm from 'shared/hooks/useForm';
import { isStringValid } from 'shared/lib/validators';
import Loadable from 'shared/components/Loadable';
import Subheader from 'shared/components/Subheader';
import { actions } from 'userportal/ducks/auth';
import Page from 'userportal/components/Page';
import apiClient from 'userportal/lib/ApiClient';
import Form from './components/Form';
import styles from './Invitation.module.scss';

export const Invitation = () => {
  const { code } = useParams();
  const dispatch = useDispatch();
  const [state, setState] = useState({ loading: false, processing: false, invitation: null, error: null });
  const { i18n, t } = useTranslation('userportal');

  const loadData = async () => {
    setState(prev => ({ ...prev, loading: true, invitation: null, error: null }));
    try {
      const invitation = await apiClient.get(`/auth/invitation/${code}`);

      if (invitation && invitation.data && invitation.data.language) {
        i18n.changeLanguage(invitation.data.language);
      }

      setState(prev => ({
        ...prev,
        loading: false,
        invitation: {
          invitationCode: invitation.code,
          email: invitation.email,
          firstname: invitation.firstname,
          lastname: invitation.lastname,
          password: '',
          passwordCheck: '',
          agreement: false,
          lookingForJob: Number.isInteger(invitation.data.lookingForJob) ? invitation.data.lookingForJob : LOOKING_FOR_JOB.MAYBE,
          createdByUser: invitation.createdByUser,
          primaryFieldId: invitation.data.primaryFieldId || 1,
          preferredLanguage: invitation.data.language || DEFAULT_LANGUAGE_CODE,
          nativeLanguage: invitation.data.nativeLanguage || DEFAULT_LANGUAGE_ID,
        }
      }));
    } catch (err) {
      setState(prev => ({ ...prev, loading: false, error: err.error }));
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const finishInvitation = async (data) => {
    setState(prev => ({ ...prev, processing: true, error: null }));

    try {
      const { createdByUser, createdByCompany, ...payload } = data;
      const response = await apiClient.post('/auth/invitation', payload);
      dispatch(actions.loginAfterSignup(response));
    } catch (e) {
      setState(prev => ({ ...prev, processing: false, error: e.error }));
    }
  };

  const validate = (data) => {
    const errors = {};

    if (!isStringValid(data.firstname)) {
      errors.firstname = t('common:validation.fillFirstname');
    }

    if (!isStringValid(data.lastname)) {
      errors.lastname = t('common:validation.fillLastname');
    }

    if (!isStringValid(data.password)) {
      errors.password = t('common:validation.fillPassword');
    }

    if (data.password !== data.passwordCheck) {
      errors.password = true;
      errors.passwordCheck = t('common:validation.passwordsDoesNotMatch');
    }

    if (!data.agreement) {
      errors.agreement = t('common:validation.notCheckedAgreement');
    }

    return errors;
  };

  const { values, setValues, errors, hasError, handleChange, handleSubmit, resetErrors } = useForm(finishInvitation, {}, validate);
  if (state.invitation && state.invitation.invitationCode && !values.invitationCode) {
    setValues({ ...state.invitation });
  }

  const onPasswordChange = (event) => {
    handleChange(event);
    resetErrors(['password', 'passwordCheck']);
  };

  return (
    <Page
      initialRow={false}
      subheader={(
        <Subheader center title={t('page.invitation.title')}>
          <span>{t('page.invitation.subtitle')}</span>
        </Subheader>
      )}
    >
      <Loadable
        error={state.error}
        loading={state.loading === true || state.invitation === null}
        helmetTitle={t('page.invitation.title')}
        render={() => (
          <>
            {state.invitation.createdByUser !== null && (
              <div className="row justify-content-center">
                <div className="col-xl-6 col-lg-8 col-md-8 col-sm-12 col-xs-12">
                  <p className={styles.senderInfo}>
                    <Trans
                      i18nKey="userportal:page.invitation.invitationBy"
                      values={{ name: t('common:name'), username: `${state.invitation.createdByUser.firstname} ${state.invitation.createdByUser.lastname}` }}
                    >
                      <strong />
                      <strong />
                    </Trans>
                  </p>
                </div>
              </div>
            )}
            <div className="row justify-content-center">
              <div className="col-xl-6 col-lg-8 col-md-8 col-sm-12 col-xs-12">
                <Form
                  email={state.invitation.email}
                  values={values}
                  hasError={hasError}
                  errors={errors}
                  onChange={handleChange}
                  onPasswordChange={onPasswordChange}
                  onSubmit={handleSubmit}
                  processing={state.processing}
                />
              </div>
            </div>
          </>
        )}
      />
    </Page>
  );
};

export default Invitation;
